import { Fragment, useCallback, useMemo } from 'react';
import type { MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TertiaryNavigationActionButton,
  TertiaryNavigationActionTransition,
  TertiaryNavigationAddButton,
  TertiaryNavigationAiButton,
  TertiaryNavigationAiPrompt,
  TertiaryNavigationCheckboxAction,
  TertiaryNavigationCurrencySelector,
  TertiaryNavigationIconButton,
  type TertiaryNavigationItem,
  TertiaryNavigation as TertiaryNavigationPrimitive,
  TertiaryNavigationSearch,
  TertiaryNavigationSkeleton,
  TertiaryNavigationSplitActionButton,
} from 'components/ui';
import { useFirmBaseLayoutContext } from 'context/FirmBaseLayoutContext';
import type { TertiaryNavigationControlConfig } from 'context/FirmBaseLayoutContext';
import { getTertiaryNavigationActiveValue } from 'context/FirmBaseLayoutContext/tertiaryNavigation.utilities';
import { useZoom } from 'context/ZoomContext';

const getTertiaryNavigationControlKey = (control: TertiaryNavigationControlConfig, index: number) =>
  control.key ?? `${control.type}-${index}`;

const renderTertiaryNavigationControl = (control: TertiaryNavigationControlConfig, index: number) => {
  const key = getTertiaryNavigationControlKey(control, index);
  const ariaHasPopup = 'hasPopup' in control && control.hasPopup ? 'menu' : undefined;
  const isVisible = control.isVisible ?? true;
  const isDisabled = control.disabled || !isVisible;

  const controlNode = (() => {
    switch (control.type) {
      case 'addButton':
        return (
          <TertiaryNavigationAddButton
            aria-haspopup={ariaHasPopup}
            data-testid={control.testId}
            disabled={isDisabled}
            onClick={control.onClick}
          />
        );
      case 'aiButton':
        return (
          <TertiaryNavigationAiButton
            aria-haspopup={ariaHasPopup}
            ariaLabel={control.ariaLabel}
            data-testid={control.testId}
            disabled={isDisabled}
            icon={control.icon}
            onClick={control.onClick}
          />
        );
      case 'aiPrompt':
        return (
          <TertiaryNavigationAiPrompt
            ariaLabel={control.ariaLabel}
            busyHint={control.busyHint}
            collapsedTooltipTitle={control.collapsedTooltipTitle}
            disabled={isDisabled}
            icon={control.icon}
            isBusy={control.isBusy}
            maxRows={control.maxRows}
            onOpenChange={control.onOpenChange}
            onSubmit={control.onSubmit}
            panelAriaLabel={control.panelAriaLabel}
            panelIcon={control.panelIcon}
            placeholder={control.placeholder}
            submitAriaLabel={control.submitAriaLabel}
            submitHint={control.submitHint}
            testId={control.testId}
          />
        );
      case 'button':
        return (
          <TertiaryNavigationActionButton
            aria-haspopup={ariaHasPopup}
            data-testid={control.testId}
            disabled={isDisabled}
            endIcon={control.endIcon}
            errorLabel={control.errorLabel}
            isSaving={control.isSaving}
            locked={control.locked}
            onClick={control.onClick}
            saveFailed={control.saveFailed}
            savedLabel={control.savedLabel}
            savingLabel={control.savingLabel}
            startIcon={control.startIcon}
            variant={control.variant}>
            {control.label}
          </TertiaryNavigationActionButton>
        );
      case 'checkbox':
        return (
          <TertiaryNavigationCheckboxAction
            checked={control.checked}
            data-testid={control.testId}
            disabled={isDisabled}
            indeterminate={control.indeterminate}
            inputProps={{
              'aria-label': control.inputAriaLabel,
            }}
            label={control.label}
            onChange={control.onChange}
          />
        );
      case 'currencySelector':
        return (
          <TertiaryNavigationCurrencySelector
            currency={control.currency}
            currencyAriaLabel={control.currencyAriaLabel}
            currencyTooltipTitle={control.currencyTooltipTitle}
            data-testid={control.testId}
            onCurrencyClick={isDisabled ? undefined : control.onCurrencyClick}
            onUnitClick={isDisabled ? undefined : control.onUnitClick}
            unit={control.unit}
            unitAriaLabel={control.unitAriaLabel}
          />
        );
      case 'iconButton':
        return (
          <TertiaryNavigationIconButton
            aria-haspopup={ariaHasPopup}
            ariaLabel={control.ariaLabel}
            data-testid={control.testId}
            disabled={isDisabled}
            icon={control.icon}
            onClick={control.onClick}
            tooltipTitle={control.tooltipTitle}
            variant={control.variant}
          />
        );
      case 'search':
        return (
          <TertiaryNavigationSearch
            ariaLabel={control.ariaLabel}
            collapsedAriaLabel={control.collapsedAriaLabel}
            collapsedTooltipTitle={control.collapsedTooltipTitle}
            clearAriaLabel={control.clearAriaLabel}
            commitMode={control.commitMode}
            commitDelayMs={control.commitDelayMs}
            collapsible={control.collapsible}
            disabled={isDisabled}
            isSuggestionsLoading={control.isSuggestionsLoading}
            onChange={control.onChange}
            onInputValueChange={control.onInputValueChange}
            onSuggestionSelect={control.onSuggestionSelect}
            placeholder={control.placeholder}
            showEmptySuggestions={control.showEmptySuggestions}
            startIcon={control.startIcon}
            submitAriaLabel={control.submitAriaLabel}
            submitHint={control.submitHint}
            suggestions={control.suggestions}
            suggestionsEmptyLabel={control.suggestionsEmptyLabel}
            suggestionsLoadingLabel={control.suggestionsLoadingLabel}
            testId={control.testId}
            value={control.value}
          />
        );
      case 'splitButton':
        return (
          <TertiaryNavigationSplitActionButton
            aria-haspopup={ariaHasPopup}
            data-testid={control.testId}
            disabled={isDisabled}
            errorLabel={control.errorLabel}
            isSaving={control.isSaving}
            locked={control.locked}
            mainDisabled={control.mainDisabled}
            menuAriaLabel={control.menuAriaLabel}
            menuDisabled={control.menuDisabled}
            onClick={control.onClick}
            onMenuClick={control.onMenuClick}
            saveFailed={control.saveFailed}
            savedLabel={control.savedLabel}
            savingLabel={control.savingLabel}
            variant={control.variant}>
            {control.label}
          </TertiaryNavigationSplitActionButton>
        );
      default:
        return null;
    }
  })();

  if (!controlNode) {
    return null;
  }

  if (control.isVisible === undefined) {
    return <Fragment key={key}>{controlNode}</Fragment>;
  }

  return (
    <TertiaryNavigationActionTransition key={key} isVisible={isVisible}>
      {controlNode}
    </TertiaryNavigationActionTransition>
  );
};

const renderTertiaryNavigationControls = (controls: TertiaryNavigationControlConfig[] = []) =>
  controls.map(renderTertiaryNavigationControl);

interface FirmBaseTertiaryNavigationProps {
  'data-tour'?: string;
}

const TertiaryNavigation = (rootProps: FirmBaseTertiaryNavigationProps) => {
  const { tertiaryNavigation } = useFirmBaseLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { zoomLevel } = useZoom();

  const items = useMemo(() => tertiaryNavigation?.items ?? [], [tertiaryNavigation?.items]);

  const activeValue = useMemo(
    () => getTertiaryNavigationActiveValue({
      currentPathname: location.pathname,
      currentSearch: location.search,
      items,
      value: tertiaryNavigation?.value,
    }),
    [items, location.pathname, location.search, tertiaryNavigation?.value]
  );

  const handleChange = useCallback(
    (value: string) => {
      const selectedItem = items.find(item => item.value === value);

      if (!selectedItem || selectedItem.disabled) {
        return;
      }

      selectedItem.onSelect?.();

      if (selectedItem.to) {
        navigate(selectedItem.to);
      }
    },
    [items, navigate]
  );

  const handleItemClose = useCallback(
    (_event: MouseEvent<HTMLButtonElement>, item: TertiaryNavigationItem) => {
      items.find(sourceItem => sourceItem.value === item.value)?.onClose?.();
    },
    [items]
  );

  const navigationItems = useMemo(
    () =>
      items.map(item => ({
        closeAriaLabel: item.closeAriaLabel,
        disabled: item.disabled || (!item.to && !item.onSelect),
        hasError: item.hasError,
        isClosable: item.isClosable,
        label: item.label,
        showMenu: item.showMenu,
        value: item.value,
      })),
    [items]
  );

  const leftActions = useMemo(() => {
    const leftActionControls = renderTertiaryNavigationControls(tertiaryNavigation?.leftActions);

    if (!leftActionControls.length && !tertiaryNavigation?.leftActionsNode) {
      return undefined;
    }

    return (
      <>
        {leftActionControls}
        {tertiaryNavigation?.leftActionsNode}
      </>
    );
  }, [tertiaryNavigation?.leftActions, tertiaryNavigation?.leftActionsNode]);

  const actions = useMemo(() => {
    const actionControls = renderTertiaryNavigationControls(tertiaryNavigation?.actions);

    if (!actionControls.length && !tertiaryNavigation?.actionsNode) {
      return undefined;
    }

    return (
      <>
        {tertiaryNavigation?.actionsNode}
        {actionControls}
      </>
    );
  }, [tertiaryNavigation?.actions, tertiaryNavigation?.actionsNode]);

  if (!tertiaryNavigation) {
    return null;
  }

  if (tertiaryNavigation.isLoading) {
    return (
      <TertiaryNavigationSkeleton
        ariaLabel={tertiaryNavigation.ariaLabel}
        zoomLevel={zoomLevel}
        {...tertiaryNavigation.skeletonProps}
      />
    );
  }

  return (
    <TertiaryNavigationPrimitive
      actions={actions}
      ariaLabel={tertiaryNavigation.ariaLabel}
      items={navigationItems}
      leftActions={leftActions}
      onChange={handleChange}
      onItemClose={handleItemClose}
      onItemMenuClick={tertiaryNavigation.onItemMenuClick}
      value={activeValue}
      zoomLevel={zoomLevel}
      {...rootProps}
    />
  );
};

export default TertiaryNavigation;
